import React from 'react';

const clsx = (...parts: Array<string | false | null | undefined>): string => parts.filter(Boolean).join(' ');

export interface PillOption<T> {
    value: T;
    label: string;
    icon?: React.ReactNode;
    disabled?: boolean;
    title?: string;
}

interface PillToggleProps<T> {
    options: PillOption<T>[];
    value: T;
    onChange: (value: T) => void;
    disabled?: boolean;
    /** Show text labels (default: true if no icons, false if icons provided) */
    showLabels?: boolean;
    /** Size variant */
    size?: 'xs' | 'sm' | 'md';
    /** Icon to show when the whole toggle is disabled/locked */
    lockedIcon?: React.ReactNode;
    /** Custom container className */
    className?: string;
}

export function PillToggle<T>({
    options,
    value,
    onChange,
    disabled = false,
    showLabels,
    size = 'sm',
    lockedIcon,
    className,
}: PillToggleProps<T>) {
    const hasIcons = options.some((opt) => opt.icon);
    const shouldShowLabels = showLabels ?? !hasIcons;

    const sizeClasses = {
        xs: {
            container: 'gap-0.5 px-1 py-0.5',
            option: shouldShowLabels ? 'px-1.5 py-0.5' : 'w-5 h-5',
            text: 'text-[10px]',
            lockedIcon: 'w-4 h-4',
        },
        sm: {
            container: 'gap-1 px-1.5 py-1',
            option: shouldShowLabels ? 'px-2 py-1' : 'w-7 h-7',
            text: 'text-xs',
            lockedIcon: 'w-6 h-6',
        },
        md: {
            container: 'gap-2 px-2 py-1.5',
            option: shouldShowLabels ? 'px-3 py-1.5' : 'w-8 h-8',
            text: 'text-sm',
            lockedIcon: 'w-7 h-7',
        },
    };

    const sizes = sizeClasses[size];

    // Cycle to next option
    const handleCycle = () => {
        if (disabled) return;
        const currentIdx = options.findIndex((opt) => opt.value === value);
        const nextIdx = (currentIdx + 1) % options.length;
        onChange(options[nextIdx].value);
    };

    // When locked and lockedIcon is provided, show only the locked icon
    if (disabled && lockedIcon) {
        return (
            <div
                className={clsx(
                    'flex items-center rounded-full border border-gray-200 bg-white transition-all duration-200',
                    sizes.container,
                    'opacity-50 cursor-not-allowed',
                    className
                )}
            >
                <div className={clsx('flex items-center justify-center text-gray-400', sizes.lockedIcon)}>
                    {lockedIcon}
                </div>
            </div>
        );
    }

    return (
        <div
            onClick={handleCycle}
            className={clsx(
                'flex items-center rounded-full border border-gray-200 bg-white transition-all duration-200',
                sizes.container,
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50',
                className
            )}
        >
            {options.map((option) => {
                const isActive = option.value === value;

                return (
                    <div
                        key={String(option.value)}
                        title={option.title ?? option.label}
                        className={clsx(
                            'relative group flex items-center justify-center rounded-full transition-all duration-200 select-none pointer-events-none',
                            sizes.option,
                            sizes.text,
                            isActive
                                ? 'bg-[#3a3a4a] text-white shadow-sm scale-105'
                                : 'bg-transparent text-gray-500',
                            option.disabled && 'opacity-40'
                        )}
                    >
                        {option.icon && (
                            <span className={shouldShowLabels ? 'mr-1' : ''}>
                                {option.icon}
                            </span>
                        )}
                        {shouldShowLabels && option.label}

                        {/* Hover tooltip for icon-only mode */}
                        {!shouldShowLabels && (
                            <span className="absolute -top-7 left-1/2 -translate-x-1/2 text-[10px] text-gray-600 bg-white/80 rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition whitespace-nowrap z-10">
                                {option.label}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default PillToggle;